import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone } from "lucide-react";
import apiClient from "../../config/apiClient";
import Flag from "react-world-flags";
import {
  FaLinkedin,
  FaInstagram,
  FaYoutube,
  FaFacebook,
} from "react-icons/fa6";
import { SiX } from "react-icons/si";
import logo_light from "../../assets/new-images/darkmodelogo.png";
import { footerLinks, footerCompany } from "../../data/data";

const socialLinks = [
  { icon: <FaLinkedin />, to: "https://www.linkedin.com/company/simplifyte/", title: "LinkedIn" },
  { icon: <FaInstagram />, to: "https://www.instagram.com/simplifytelabs", title: "Instagram" },
  { icon: <SiX />, to: "https://x.com/simplifyte", title: "X / Twitter" },
  { icon: <FaYoutube />, to: "https://www.youtube.com/@SimplifyteLabs", title: "YouTube" },
  { icon: <FaFacebook />, to: "https://www.facebook.com/people/Simplifyte/61591003062575/", title: "Facebook" },
];

export default function CustomFooter() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post("/subscribe", { email });
      setMessage(response.data?.message || "Subscribed successfully!");
      setEmail("");
    } catch (error) {
      console.error(error);
      setMessage(error?.response?.data?.message || "❌ Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-dark-footer text-gray-200">
      <div className="container mx-auto px-6 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Col 1 — Brand */}
          <div className="flex flex-col gap-5">
            <Link to="/" className="inline-block">
              <img src={logo_light} alt="Simplifyte" className="h-10" />
            </Link>

            {/* <p className="text-gray-400 text-sm leading-relaxed">
              Workflow management software for CA firms, tax consultants, advocates and professional service teams.
            </p> */}

            {/* Contact */}
            <div className="flex flex-col gap-2">
              <a
                href="tel:+919904858858"
                className="flex items-center gap-2 text-gray-300 hover:text-white transition duration-200 text-sm"
              >
                <Phone size={14} className="text-gray-300 shrink-0" />
                +91  990 485 8858
              </a>
              <a
                href="mailto:support@simplifyte.com"
                className="flex items-center gap-2 text-gray-300 hover:text-white transition duration-200 text-sm"
              >
                <Mail size={14} className="text-gray-300 shrink-0" />
                sales@simplifyte.com
              </a>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-3">
              {socialLinks.map((s, i) => (
                <Link
                  key={i}
                  to={s.to}
                  target="_blank"
                  title={s.title}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-700 hover:bg-indigo-600 text-gray-300 hover:text-white transition duration-200 text-sm"
                >
                  {s.icon}
                </Link>
              ))}
            </div>

            <p className="flex items-center gap-1.5 text-gray-500 text-xs font-medium">
              Made in <Flag code="IN" style={{ width: 20, height: 14 }} />
            </p>
          </div>

          {/* Col 2 — Company */}
          <div>
            <h5 className="font-semibold text-white text-sm uppercase tracking-wider mb-5">Company</h5>
            <ul className="space-y-3">
              {footerCompany.map((data, index) => (
                <li key={index}>
                  <Link
                    to={data.route}
                    className="text-gray-400 hover:text-white text-sm transition duration-200"
                  >
                    {data.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Help & Legal */}
          <div>
            <h5 className="font-semibold text-white text-sm uppercase tracking-wider mb-5">Help & Legal</h5>
            <ul className="space-y-3">
              {footerLinks.map((data, index) => (
                <li key={index}>
                  <Link
                    to={data.route}
                    className="text-gray-400 hover:text-white text-sm transition duration-200"
                  >
                    {data.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 — Newsletter */}
          <div>
            <h5 className="font-semibold text-white text-sm uppercase tracking-wider mb-5">Stay in the loop</h5>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              Product updates, tips and early access perks — straight to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col gap-3">
              <div className="relative">
                <Mail size={15} className="absolute top-1/2 -translate-y-1/2 left-3 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full pl-9 pr-4 py-2.5 bg-slate-800 text-white text-sm rounded-lg border border-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none transition"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-white text-sm font-semibold transition duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {loading ? "Subscribing..." : "Subscribe"}
              </button>
              {message && <p className="text-xs text-green-400">{message}</p>}
            </form>
          </div>

        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Simplifyte Labs Pvt. Ltd. All rights reserved.
          </p>
          {/* <div className="flex items-center gap-5">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-gray-300 text-xs transition duration-200">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-500 hover:text-gray-300 text-xs transition duration-200">Terms of Service</Link>
            <Link to="/policy-refund" className="text-gray-500 hover:text-gray-300 text-xs transition duration-200">Refund Policy</Link>
          </div> */}
        </div>
      </div>
    </footer>
  );
}
